import React, { useEffect, useMemo, useState } from 'react';
import { TreeSelect } from 'antd';
import api from '../../services/api';
import { useHrOptions } from '../../hooks/useHrOptions';
import { buildEmployeeDeptTreeSelectProps } from '../../utils/orgTree';

/**
 * 统一的「中心/项目」组织树选择器。
 * 用于员工归属、计划下发、考勤范围、物资归属等场景。
 * scoped=true 时从后端拉取当前用户数据权限内的组织（物资模块）。
 */
const PROJECT_DEPT_SELECTABLE_TYPES = new Set([2, 3, 4]);

export default function EmployeeDeptOrgTreeSelect({
  orgs: orgsProp,
  multiple = false,
  scoped = false,
  scopedApiPath = '/materials/orgs/options',
  allowProjectDept = false,
  ...rest
}) {
  const { allOrgs } = useHrOptions();
  const [scopedOrgs, setScopedOrgs] = useState([]);
  const [scopedLoading, setScopedLoading] = useState(false);

  useEffect(() => {
    if (!scoped || orgsProp) return undefined;
    let cancelled = false;
    setScopedLoading(true);
    api.get(scopedApiPath)
      .then((res) => {
        if (!cancelled && res.data.success) setScopedOrgs(res.data.data || []);
      })
      .catch(() => {
        if (!cancelled) setScopedOrgs([]);
      })
      .finally(() => {
        if (!cancelled) setScopedLoading(false);
      });
    return () => { cancelled = true; };
  }, [scoped, scopedApiPath, orgsProp]);

  const orgs = orgsProp ?? (scoped ? scopedOrgs : allOrgs);
  const respectSelectableFlag = scoped && !orgsProp && orgs.some((org) => org.selectable !== undefined);

  const treeSelectProps = useMemo(
    () => buildEmployeeDeptTreeSelectProps(orgs, {
      multiple,
      respectSelectableFlag,
      selectableOrgTypes: allowProjectDept ? PROJECT_DEPT_SELECTABLE_TYPES : undefined,
      placeholder: rest.placeholder ?? (allowProjectDept ? '选择归属项目/部门' : undefined)
    }),
    [orgs, multiple, respectSelectableFlag, allowProjectDept, rest.placeholder]
  );

  return <TreeSelect {...treeSelectProps} loading={scoped && scopedLoading} {...rest} />;
}
